import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './EventDetails.css';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageOrientation, setImageOrientation] = useState('landscape'); // 'landscape' or 'portrait'

  useEffect(() => {
    fetchEventDetails();
  }, [id]);

  useEffect(() => {
    if (event && event.image_path) {
      handleImageLoad(`https://admin.pakschoolandcollege.com/${event.image_path}`);
    }
  }, [event]);

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(`https://admin.pakschoolandcollege.com/api/events.php`);
      const data = await response.json();
      
      if (data.success) {
        const foundEvent = data.data.find(e => e.id == id);
        if (foundEvent) {
          setEvent(foundEvent);
        } else {
          setError('Event not found');
        }
      } else {
        setError('Failed to fetch event details');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError('Error loading event details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const detectImageOrientation = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const orientation = img.height > img.width ? 'portrait' : 'landscape';
        resolve(orientation);
      };
      img.onerror = () => {
        resolve('landscape'); // Default to landscape if image fails to load
      };
      img.src = imageUrl;
    });
  };

  const handleImageLoad = async (imageUrl) => {
    if (imageUrl) {
      const orientation = await detectImageOrientation(imageUrl);
      setImageOrientation(orientation);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <i className="fas fa-exclamation-triangle text-6xl text-red-500 mb-6"></i>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Event Not Found</h3>
            <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has been removed.</p>
            <button 
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200"
              onClick={() => navigate('/')}
            >
              <i className="fas fa-home mr-2"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-8">
          <button 
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Events
          </button>
        </div>

        <div className={`grid grid-cols-1 gap-8 ${
          imageOrientation === 'portrait' 
            ? 'lg:grid-cols-2' 
            : 'lg:grid-cols-3'
        }`}>
          <div className={`${
            imageOrientation === 'portrait' 
              ? 'lg:col-span-1' 
              : 'lg:col-span-2'
          }`}>
            {/* Event Image */}
            {event.image_path && (
              <div className="mb-8">
                <div className={`relative rounded-2xl overflow-hidden shadow-2xl ${
                  imageOrientation === 'portrait' 
                    ? 'h-auto max-h-[80vh] w-full' 
                    : 'h-96 w-full'
                }`}>
                  <img 
                    src={`https://admin.pakschoolandcollege.com/${event.image_path}`}
                    alt={event.name}
                    className={`w-full ${
                      imageOrientation === 'portrait' 
                        ? 'h-auto object-contain' 
                        : 'h-full object-cover'
                    }`}
                    onLoad={() => handleImageLoad(`https://admin.pakschoolandcollege.com/${event.image_path}`)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
            )}

            {/* Event Content */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex justify-between items-start mb-6">
                <h1 className="text-4xl font-bold text-gray-800 leading-tight">{event.name}</h1>
                {event.featured && (
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    <i className="fas fa-star mr-1"></i>
                    Featured
                  </span>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      <i className="fas fa-calendar-alt text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Date</p>
                      <p className="text-lg font-semibold text-gray-800">{formatDate(event.event_date)}</p>
                    </div>
                  </div>
                  
                  {event.event_time && (
                    <div className="flex items-center">
                      <div className="bg-green-100 p-3 rounded-lg mr-4">
                        <i className="fas fa-clock text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Time</p>
                        <p className="text-lg font-semibold text-gray-800">{formatTime(event.event_time)}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {event.location && (
                  <div className="flex items-center mt-4">
                    <div className="bg-red-100 p-3 rounded-lg mr-4">
                      <i className="fas fa-map-marker-alt text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Location</p>
                      <p className="text-lg font-semibold text-gray-800">{event.location}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">About This Event</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {event.description || 'No description available for this event.'}
                </p>
              </div>

              <div className="flex items-center">
                <h3 className="text-xl font-bold text-gray-800 mr-4">Event Status:</h3>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                  event.status === 'ongoing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                </span>
              </div>
            </div>
          </div>

          <div className={`${
            imageOrientation === 'portrait' 
              ? 'lg:col-span-1' 
              : 'lg:col-span-1'
          }`}>
            {/* Event Info Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h4 className="text-xl font-bold text-gray-800 mb-6 pb-4 border-b border-gray-200">Event Information</h4>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-lg mr-4">
                    <i className="fas fa-calendar text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Date</p>
                    <p className="text-gray-600">{formatDate(event.event_date)}</p>
                  </div>
                </div>

                {event.event_time && (
                  <div className="flex items-start">
                    <div className="bg-green-100 p-3 rounded-lg mr-4">
                      <i className="fas fa-clock text-green-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Time</p>
                      <p className="text-gray-600">{formatTime(event.event_time)}</p>
                    </div>
                  </div>
                )}

                {event.location && (
                  <div className="flex items-start">
                    <div className="bg-red-100 p-3 rounded-lg mr-4">
                      <i className="fas fa-map-marker-alt text-red-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Location</p>
                      <p className="text-gray-600">{event.location}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start">
                  <div className="bg-purple-100 p-3 rounded-lg mr-4">
                    <i className="fas fa-info-circle text-purple-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Status</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                      event.status === 'ongoing' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                    </span>
                  </div>
                </div>

                {event.featured && (
                  <div className="flex items-start">
                    <div className="bg-yellow-100 p-3 rounded-lg mr-4">
                      <i className="fas fa-star text-yellow-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Special</p>
                      <p className="text-gray-600">Featured Event</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;
